import React, { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useFeature } from '@growthbook/growthbook-react';
import debounce from '@mui/utils/debounce';
import { v4 as uuidv4 } from 'uuid';

import { InviteMemberTypes, MemberField } from '@/components/InviteMember';
import { InviteOptions } from '@/components/Steps/InviteTeamStep/InviteOptions';
import { useAppDispatch, useAppSelector, useOnboardingTask, useTracking } from '@/hooks';
import { FlowModel } from '@/models/flow.model';
import { selectCompanyId, selectUserEmail } from '@/redux/features/authSlice';
import { setInviteSuccessCount, setMembersToInvite } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import UserRequests from '@/requests/user.requests';
import { AccessLevel, ExperimentProps, Member, StepCommonProps } from '@/types/index';

import { useDownloadApps } from '../../../hooks/useDownloadApps';

import {
  AUTOMATIC_TRACKING_ACCESS_LEVELS,
  defaultInviteMemberRole,
  isAutomaticTrackingMode,
  normalizeMemberAccessLevel,
} from './inviteAccessLevels.util';
import { resolvedInviteStepOptimizationVariationId } from './inviteStepOptimization.util';

const DEFAULT_DEBOUNCE_TIME = 100;

const defaultMember = (isAutomaticTracking: boolean): Member => ({
  id: uuidv4(),
  email: '',
  role: defaultInviteMemberRole(isAutomaticTracking),
});

type InviteFormProps = {
  initialMembersToInvite?: number;
  trackSkipComponent?: string;
  flow: FlowModel;
} & StepCommonProps;

export const InviteFormComponent: FC<InviteFormProps> = ({ flow }) => {
  const ownerEmail = useSelector(selectUserEmail);
  const companyId = useSelector(selectCompanyId);
  const dispatch = useAppDispatch();
  const { trackAppEvent, trackOnboardingStep } = useTracking(flow);
  const { goToNextStep, goToPreviousStep } = useOnboardingTask(flow);
  const inviteStepOptimization = useFeature<ExperimentProps>('invite-step-optimization');
  const inviteOptimizationVariant =
    resolvedInviteStepOptimizationVariationId(inviteStepOptimization.value) ?? 'A';
  const { executeBrowserPromoTracking } = useDownloadApps(flow);
  const didCopyLink = useAppSelector((state) => state.onboarding.didCopyMagicLink);
  const [didTrackInputClick, setTrackInputClick] = useState(false);
  const [didTrackInputChange, setTrackInputChange] = useState(false);
  const reduxMembers = useAppSelector(state => state.onboarding.membersToInvite);
  const isEUMember = useAppSelector(state => state.onboarding.isEUMember);
  const isEeaOrUkCompany = useAppSelector(state => state.onboarding.isEeaOrUkCompany);
  const company = useAppSelector(state => state.auth.company);
  const trackingMode = useAppSelector(state => state.onboarding.trackingMode);
  const trackingModeStep = useAppSelector(state => state.onboarding.trackingModeStep);
  const customTrackingModeStep = company?.companySettings?.custom?.trackingModeStep;
  const isAutomaticTracking = isAutomaticTrackingMode(
    flow.trackingMode,
    trackingMode,
    trackingModeStep,
    customTrackingModeStep,
  );
  const allowedAccessLevels = isAutomaticTracking ? AUTOMATIC_TRACKING_ACCESS_LEVELS : undefined;
  const backendMembers = company?.companySettings?.custom?.memberstoinvite ?? reduxMembers;

  const getMergedCustomSettings = () => ({
    ...company?.companySettings?.custom,
    isUEMember: company?.companySettings?.custom?.isUEMember ?? isEUMember,
    isEeaOrUkCompany: company?.companySettings?.custom?.isEeaOrUkCompany ?? isEeaOrUkCompany,
  });

  const getInitialMembers = (): Member[] => {
    let members: Member[];
    if (reduxMembers && reduxMembers.length > 0) members = reduxMembers;
    else if (backendMembers && backendMembers.length > 0) members = backendMembers;
    else members = [defaultMember(isAutomaticTracking)];

    return isAutomaticTracking
      ? members.map(member => normalizeMemberAccessLevel(member, true))
      : members;
  };

  const [membersToInvite, setMembersToInviteState] = useState<Member[]>(getInitialMembers());

  const formProps = useForm({ mode: 'all' });
  const { handleSubmit, setError: setFormError, trigger, unregister } = formProps;

  const persistToBackend = (members: Member[]) => {
    CompanyRequests.updateMembersToInvite(companyId, members, getMergedCustomSettings());
  };

  const persistMembersToInvite = debounce(persistToBackend, 1000);

  const handleAddMoreClicked = () => {
    trackAppEvent('Clicked Add More On Onboarding Invite Screen');
    const newMember = defaultMember(isAutomaticTracking);
    setMembersToInviteState(prev => {
      const updatedMembers = [...prev, newMember];
      dispatch(setMembersToInvite(updatedMembers));
      persistMembersToInvite(updatedMembers);
      return updatedMembers;
    });
  };

  const handleAccessLvlChange = (accessLvl: AccessLevel, id: string) => {
    const updatedMembers = membersToInvite.map(member =>
      member.id === id ? { ...member, role: accessLvl } : member);
    setMembersToInviteState(updatedMembers);
    dispatch(setMembersToInvite(updatedMembers));
    persistMembersToInvite(updatedMembers);
  };

  useEffect(() => {
    const initial = getInitialMembers();
    setMembersToInviteState(initial);
    if (initial.length === 0) return;
    const emailFields = initial.map(member => `email-${ member.id }`);
    requestAnimationFrame(() => {
      trigger(emailFields).then(() => {
        emailFields.forEach((fieldName) => {
          const currentValue = formProps.getValues(fieldName);
          if (currentValue && currentValue.trim() !== '') {
            formProps.setValue(fieldName, currentValue, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }
        });
      });
    });
    // Only re-run when members source changes; formProps/trigger are stable from react-hook-form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxMembers, backendMembers]);

  const onInvalidFormSubmit = async () => {
    trackAppEvent('Failed Onboarding Invite Submission', { reason: 'invalid email address' });
  };

  const onValidFormSubmit = async (data: Record<string, string>) => {
    const isVariantC = inviteOptimizationVariant === 'C';
    const allowEmptyBecauseCopiedLink = !isVariantC && didCopyLink && !isAutomaticTracking;
    if (!allowEmptyBecauseCopiedLink && hasAllEmailsEmpty(data)) {
      const activeKeys = membersToInvite.map(member => `email-${ member.id }`);
      const firstEmptyKey = activeKeys.find(key => !data[key]?.trim());
      if (firstEmptyKey) {
        setFormError(firstEmptyKey, {
          type: 'async',
          message: 'Please provide at least one email',
        });
      }
      trackAppEvent('Failed Onboarding Invite Submission', { reason: 'email address cannot be blank' });
      return;
    }

    if (checkRepeatedEmails(data) === -1) {
      trackAppEvent('Failed Onboarding Invite Submission', { reason: 'Duplicated email address' });
      return;
    }

    const { wasSuccessful, invitedMembers, invitedCount } = await sendInvites();

    if (wasSuccessful && invitedMembers) {
      const totalnumberofinvited = invitedMembers.length;
      const numberofuserinvited = invitedMembers.filter(member => member.role === 'user').length;
      const numberofadmininvited = invitedMembers.filter(member => member.role === 'admin').length;
      const numberofmanagerinvited = invitedMembers.filter(member => member.role === 'manager').length;

      await trackOnboardingStep('Invite User during Onboarding', {
        inviteeemails: { ...invitedMembers.map(member => member.email) },
        totalnumberofinvited,
        numberofuserinvited,
        numberofadmininvited,
        numberofmanagerinvited,
      });

      await trackOnboardingStep('Clicked Next On Onboarding Invite Screen', {
        inviteeemails: { ...invitedMembers.map(member => member.email) },
        totalnumberofinvited,
        numberofuserinvited,
        numberofadmininvited,
        numberofmanagerinvited,
      });
    }

    UserRequests.updateSettings(companyId, {
      custom: {
        ...company?.companySettings?.custom,
        completedStep: { addEmployee: true },
      },
    });

    await executeBrowserPromoTracking();

    const hasLinkShareStep = flow.getSteps().some(step => step.id === 'inviteLinkShareStep');
    if (isVariantC && hasLinkShareStep) {
      goToNextStep({ withNextStepId: 'inviteLinkShareStep', stepData: { invitedCount }});
      return;
    }

    goToNextStep({ stepData: { invitedCount }});
  };

  const hasAllEmailsEmpty = (data: Record<string, string>) => Object.values(data).every(email => !email.trim());

  const checkRepeatedEmails = (data: Record<string, string>) => {
    let returnCode: 0 | -1 = 0;
    const dataEntries = Object.entries(data);
    const emails: string[] = [];
    for (const [key, emailEntry] of dataEntries) {
      const trimmedEmail = emailEntry.trim();
      if (!trimmedEmail) continue;
      const id = key.replace('email-', '');
      const memberIndex = membersToInvite.findIndex(m => m.id === id);
      if (memberIndex === -1) continue;
      setMembersToInviteState(prev =>
        prev.map(member => member.id === id ? { ...member, email: trimmedEmail } : member));
      if (emails.includes(trimmedEmail) || isOwnerEmail(trimmedEmail)) {
        setFormError(key, { type: 'async', message: 'This email address has already been added.' });
        returnCode = -1;
      } else {
        emails.push(trimmedEmail);
      }
    }
    return returnCode;
  };

  const isOwnerEmail = (email: string) => email === ownerEmail;

  const sendInvites = async () => {
    try {
      const filteredMembers = membersToInvite.filter(member => member.email);
      const { data: responseData } = await UserRequests.bulkInvite({ users: filteredMembers }, companyId);
      let inviteSuccessCount = 0;
      for (const [i, inviteData] of responseData.data.entries()) {
        const keyEmail = filteredMembers[i].email;
        if (inviteData[keyEmail]?.status === 'sent') {
          inviteSuccessCount++;
        }
      }
      dispatch(setInviteSuccessCount(inviteSuccessCount));
      return {
        invitedCount: inviteSuccessCount,
        wasSuccessful: inviteSuccessCount === filteredMembers.length,
        invitedMembers: filteredMembers,
      };
    } catch (e) {
      return {
        invitedCount: 0,
        wasSuccessful: false,
        invitedMembers: undefined,
      };
    }
  };

  const handleInputClick: InviteMemberTypes.InputClickFn = ({ role, email }) => {
    if (!didTrackInputClick) {
      trackAppEvent('Invite users onboarding email active', {
        inviteeusertype: role,
        inviteuseremail: email,
      });
      setTrackInputClick(true);
    }
  };

  const handleInputChange: InviteMemberTypes.InputChangeFn = debounce(
    async (inputId, { email, role }) => {
      const id = inputId.split('email-')[1];
      const isValidEmail = await trigger(inputId);
      if (!isValidEmail || isOwnerEmail(email)) return;
      const updatedMembers = membersToInvite.map(member =>
        member.id === id ? { ...member, email } : member);
      setMembersToInviteState(updatedMembers);
      persistMembersToInvite(updatedMembers);
      if (!didTrackInputChange) {
        trackAppEvent('Invite Users Onboarding typed valid email', {
          inviteeusertype: role,
          inviteuseremail: email,
        });
        setTrackInputChange(true);
      }
    },
    DEFAULT_DEBOUNCE_TIME,
  );

  const handleRemoveMemberClick = (id: string) => {
    setMembersToInviteState(prev => {
      const updated = prev.filter(member => member.id !== id);
      const memberToRemove = prev.find(m => m.id === id);
      if (memberToRemove) {
        unregister(`email-${ memberToRemove.id }`);
        trackAppEvent('Removed Member Field On Onboarding Invite Screen', {
          removememberemail: memberToRemove.email,
          remainingFields: updated.length,
        });
      }
      dispatch(setMembersToInvite(updated));
      persistMembersToInvite(updated);
      return updated;
    });
  };

  const handleBackClick = async () => {
    dispatch(setMembersToInvite(membersToInvite));
    await CompanyRequests.updateMembersToInvite(companyId, membersToInvite, getMergedCustomSettings());
    goToPreviousStep();
  };

  return (
    <FormProvider { ...formProps }>
      <InviteOptions
        flow={ flow }
        onSendInvites={ handleSubmit(onValidFormSubmit, onInvalidFormSubmit) }
        onAddMoreClick={ handleAddMoreClicked }
        membersToInvite={ membersToInvite }
        onRemoveMemberClick={ handleRemoveMemberClick }
        onBackClick={ handleBackClick }
        inviteOptimizationVariant={ inviteOptimizationVariant }
        isAutomaticTracking={ isAutomaticTracking }
        renderMemberField={ (member) => (
          <MemberField
            key={ member.id }
            inputId={ `email-${ member.id }` }
            member={ member }
            variant="exp"
            inviteOptimizationVariant={ inviteOptimizationVariant }
            onInputClick={ handleInputClick }
            onInputChange={ handleInputChange }
            onAccessLvlChange={ accessLvl => handleAccessLvlChange(accessLvl, member.id) }
            allowedAccessLevels={ allowedAccessLevels }
          />
        ) }
      />
    </FormProvider>
  );
};
